import { useQuery } from '@apollo/client';
import ErrorPage from '@website/components/utilities/ErrorPage';
import LoadingPage from '@website/components/utilities/LoadingPage';
import { GuestBookingDetailsDocument } from '@website/types/graphqlOperations';
import { DateTime } from 'luxon';
import Link from 'next/link';
import { Container, Card, Row, Breadcrumb, Col } from 'react-bootstrap';
import { Image } from '@website/components/media/utils/imgix';
import FAQs from './sections/FAQs';
import Itinerary from './sections/Itinerary';
import Host from './sections/Host';
import { checkBookingActive } from './UserBookingDetails';
import * as countries from '../../../dataObjects/countries';
import Guests from './sections/Guests';
import MeetPoints from './sections/MeetPoints';
import TripPhotos from './sections/TripPhotos';

export default function GuestIndividualBookingView({
  spaceID,
}: {
  spaceID: string;
}) {
  const { data, loading, error } = useQuery(GuestBookingDetailsDocument, {
    variables: { spaceID },
    fetchPolicy: 'cache-and-network',
  });

  // Handle loading/errors
  if (loading) return <LoadingPage />;
  if (error) return <ErrorPage error={error} />;

  const { booking } = data.guest;
  const { group, trip } = booking;

  // Create isInFuture boolean
  const isInFuture =
    DateTime.now() <
    DateTime.fromISO(group.start.utc, {
      zone: 'utc',
    });

  // Check if booking is active or totally cancelled and set variable
  const isBookingActive = checkBookingActive(booking.spaces);

  const space = {
    ...booking.spaces.find((sp) => sp.id === spaceID)!,
    guest: data.guest,
  };

  return (
    <Container className="mt-4">
      <Row>
        <Col md={7} className="mb-3">
          <Breadcrumb listProps={{ className: 'ps-0 justify-content-start' }}>
            <Link href="/" passHref legacyBehavior>
              <Breadcrumb.Item>Home</Breadcrumb.Item>
            </Link>
            <Link href={`/me/${data.guest.userID}`} passHref legacyBehavior>
              <Breadcrumb.Item>Your dashboard</Breadcrumb.Item>
            </Link>
            <Breadcrumb.Item active>
              Trip to {countries[trip.address.country]}
            </Breadcrumb.Item>
          </Breadcrumb>

          <div className="d-md-none mt-3">
            <Image
              imgKey={booking.trip.image.file.key}
              imgixParams={{
                auto: `compress,format,enhance,redeye`,
                crop: 'faces',
                fit: 'crop',
                ar: `1.5:1`,
                saturation: isBookingActive ? '0' : '-100',
              }}
              className="rounded w-100 shadow"
              sizes="100vw"
            />
          </div>

          <div className="text-block">
            <p className="subtitle">
              {DateTime.fromISO(booking.group.start.utc)
                .setZone(group.start.zone)
                .toLocaleString(DateTime.DATE_HUGE)}{' '}
              -{' '}
              {DateTime.fromISO(booking.group.end.utc)
                .setZone(group.end.zone)
                .toLocaleString(DateTime.DATE_HUGE)}
            </p>
            <h1 className="hero-heading mb-3">{trip.title}</h1>
          </div>

          <MeetPoints
            spaces={[space]}
            userID={space.guest.userID}
            booking={booking}
          />

          <Itinerary booking={booking} />

          <Guests
            spaces={[space]}
            group={booking.group}
            isInFuture={isInFuture}
            userID={data.guest.userID}
          />

          <TripPhotos group={booking.group} media={group.media.items} />

          <Host booking={booking} userID={data.guest.userID} />

          <FAQs booking={booking} />
        </Col>

        <Col md={5} className="ps-xl-5 d-none d-md-block h-100">
          <Link href={`/trips/${booking.trip.urlSlug}`} passHref>
            <Card className="border-0 shadow hover-animate">
              <Image
                imgKey={trip.image.file.key}
                imgixParams={{
                  auto: 'compress,format,enhance,redeye',
                  crop: 'faces',
                  fit: 'crop',
                  ar: `1.5:1`,
                  saturation: isBookingActive ? '0' : '-100',
                }}
                className="w-100 h-100 rounded"
                sizes="(min-width: 1400px) 490px, (min-width: 1200px) 415px, (min-width: 992px) 376px, (min-width: 768px) 276px, 100vw"
              />
            </Card>{' '}
          </Link>
        </Col>
      </Row>
    </Container>
  );
}
